import { buildApiUrl } from '../utils/apiConfig';

export interface UserInfo {
    /** Numeric User primary key. Use this for identity/path API calls (e.g. /api/athletes/{id}). */
    id?: number;
    /** Login username (email). Not a numeric id — kept for backward compatibility. */
    userId: string;
    /** Display name from User entity (populated during OAuth/registration). */
    displayName?: string;
    /** Highest-priority role — used for backward-compatible single-role routing. */
    role: string;
    /**
     * Full ordered list of all roles this user holds (highest-priority first).
     * Multi-role Clever users will have more than one entry here.
     * Use this to build a role-switcher UI or check for specific role combinations.
     */
    roles: string[];
    hasProfile: boolean;
    termsAndConditionsAccepted: boolean;
    authenticated: boolean;
    authProvider?: string;
    /** True when the user has linked Google OAuth credentials (Drive/Calendar). */
    googleConnected?: boolean;
    internalCoachPreReadEnabled?: boolean;
    manageStudentAthletesEnabled?: boolean;
}

/**
 * Fetches the current user's information from the backend
 * This endpoint reads the JWT from the HTTP-only cookie
 * and returns user metadata including role
 */
export async function fetchUserInfo(): Promise<UserInfo | null> {
    try {
        const response = await fetch(buildApiUrl('api/auth/check'), {
            method: 'GET',
            credentials: 'include',
        });

        if (!response.ok) {
            // Clear localStorage on any authentication failure
            localStorage.removeItem('userRole');
            localStorage.removeItem('userId');
            localStorage.removeItem('loggedInUserId');

            // Don't redirect here - let the RequireAuth component handle redirects
            // This function should only return authentication status
            return null;
        }

        const data = await response.json();

        // Backend returns: { status: "authenticated", userInfo: {...} }
        // Extract the nested userInfo object
        if (data && data.userInfo) {
            return data.userInfo;
        }

        return null;
    } catch (error) {
        // Clear localStorage on network errors
        localStorage.removeItem('userRole');
        localStorage.removeItem('userId');
        localStorage.removeItem('loggedInUserId');
        return null;
    }
}

/**
 * Ensures user role is loaded in localStorage
 * Fetches from backend if not present
 * Returns null and clears localStorage if authentication fails
 */
export async function ensureUserRole(): Promise<string | null> {
    // Check if role is already in localStorage
    let role = localStorage.getItem('userRole');

    if (role) {
        // Verify the role is still valid by checking with backend
        const userInfo = await fetchUserInfo();

        if (!userInfo || !userInfo.authenticated) {
            localStorage.removeItem('userRole');
            localStorage.removeItem('userId');
            localStorage.removeItem('loggedInUserId');
            return null;
        }

        return role;
    }

    // Role not in localStorage, fetch from backend
    const userInfo = await fetchUserInfo();

    if (userInfo && userInfo.authenticated && userInfo.role) {
        localStorage.setItem('userRole', userInfo.role);

        // Store the NUMERIC id (userInfo.id), not userInfo.userId (email/username).
        if (userInfo.id != null) {
            localStorage.setItem('userId', String(userInfo.id));
            localStorage.setItem('loggedInUserId', String(userInfo.id));
        }

        return userInfo.role;
    }

    // Clear localStorage if authentication failed
    localStorage.removeItem('userRole');
    localStorage.removeItem('userId');
    localStorage.removeItem('loggedInUserId');

    return null;
}

/**
 * Validates authentication status and redirects to login if invalid
 * Use this in components that need to programmatically verify authentication
 * Throws an error to prevent further code execution if not authenticated
 */
export async function requireAuth(): Promise<UserInfo> {
    const userInfo = await fetchUserInfo();

    if (!userInfo || !userInfo.authenticated) {
        // Clear any stale data
        localStorage.removeItem('userRole');
        localStorage.removeItem('userId');
        localStorage.removeItem('loggedInUserId');

        // Force redirect to login
        window.location.href = "/login?error=unauthorized";

        // Throw to prevent further execution
        throw new Error('Authentication required');
    }

    return userInfo;
}

